import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import { getCohortsPath } from '@verantos-dev/core-constants';
import { useGetNewDatasetReleasesQuery } from '@verantos-dev/data-access-datasets';
import { useGetAllOrganizationsQuery } from '@verantos-dev/data-access-organizations';
import { useCreateProjectMutation } from '@verantos-dev/data-access-projects';
import { ApiWorkflowDialogMessages, DropdownListItem } from '@verantos-dev/shared-components';

import { useProjectModalContext } from '../context/modalContext';

export const useCreateProjectModal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    user: { isInternalUser, organizationName },
  } = useAuth();

  // local state for model entry
  const [projectName, setProjectName] = useState('');
  const [selectedRelease, setSelectedRelease] = useState<DropdownListItem | undefined>();
  const [selectedOrganization, setSelectedOrganization] = useState<DropdownListItem | undefined>();
  const [isVisible, setIsVisible] = useState(false);
  const projectNameTrimmed = projectName.trim();

  // context for modal state
  const { closeDialog } = useProjectModalContext();

  // dataset release query hook
  const {
    isLoading: _isReleasesQueryLoading,
    isUninitialized: _isReleasesQueryUninitialized,
    refetch: refetchReleases,
    isFetching: _isReleasesQueryFetching,
    data: _releasesData,
    error: releasesError,
  } = useGetNewDatasetReleasesQuery(undefined);

  // orgs query hook
  const {
    data: organizationsData,
    isLoading: isOrganizationsLoading,
    error: organizationsError,
  } = useGetAllOrganizationsQuery('', {
    skip: !isInternalUser,
  });

  // set the user organization as the default selected organization
  useEffect(() => {
    const defaultOrg = organizationsData?.data?.find(
      ({ displayName }) => displayName.toLocaleLowerCase() === organizationName?.toLowerCase()
    );
    if (defaultOrg) {
      setSelectedOrganization({
        value: defaultOrg.organizationId,
        displayValue: defaultOrg.displayName,
      });
    }
  }, [isOrganizationsLoading, organizationsData?.data, setSelectedOrganization, organizationName]);

  // Project creation mutation hook
  const [
    createProject,
    { isLoading: isCreateProjectLoading, error: createProjectError, data: createProjectResponse },
  ] = useCreateProjectMutation();

  // retry function for initial loading fetch
  const onRetryInitialLoadingFetch = () => {
    refetchReleases();
  };

  // handle project name change
  const onChangeProjectName = useCallback(
    (_: string, value: string) => {
      setProjectName(value);
    },
    [setProjectName]
  );

  const onChangeRelease = useCallback(
    (_: SyntheticEvent, value: DropdownListItem | DropdownListItem[] | null) => {
      if (value && !Array.isArray(value)) {
        setSelectedRelease(value);
      }
    },
    [setSelectedRelease]
  );

  const onChangeOrganization = useCallback(
    (_: SyntheticEvent, value: DropdownListItem | DropdownListItem[] | null) => {
      if (value && !Array.isArray(value)) {
        setSelectedOrganization(value);
      }
    },
    [setSelectedOrganization]
  );

  const onChangeIsVisible = useCallback(
    (value: string) => setIsVisible(value === 'true'),
    [setIsVisible]
  );

  // handle form submission
  const onSubmit = async () => {
    if (!selectedRelease || !projectNameTrimmed) {
      return;
    }
    try {
      await createProject({
        projectName: projectNameTrimmed,
        datasetReleaseId: selectedRelease?.value || '',
        datasetReleaseName: selectedRelease?.displayValue?.toString() || '',
        ...(isInternalUser && selectedOrganization?.value
          ? {
              organizationId: selectedOrganization?.value,
              organizationName: selectedOrganization?.displayValue?.toString(),
              isVisible:
                selectedOrganization?.displayValue?.toString().toLocaleLowerCase() ===
                organizationName?.toLocaleLowerCase()
                  ? true
                  : isVisible,
            }
          : {}),
      });
    } catch (error) {
      console.error('Error creating project', error);
    }
  };
  // handle redirect after successful
  const onRedirectAfterSubmit = () => {
    navigate(getCohortsPath(createProjectResponse?.data.projectId || ''));
  };

  // messages for the modal content area
  const createProjectModalContentMessages = {
    projectNameLabel: intl.formatMessage({
      id: 'projectsList.createProjectModal.projectName.label',
    }),
    projectNamePlaceholder: intl.formatMessage({
      id: 'projectsList.createProjectModal.projectName.placeholder',
    }),
    datasetLabel: intl.formatMessage({ id: 'projectsList.createProjectModal.dataset.label' }),
    datasetPlaceholder: intl.formatMessage({
      id: 'projectsList.createProjectModal.dataset.placeholder',
    }),
    organizationLabel: intl.formatMessage({
      id: 'projectsList.createProjectModal.organization.label',
    }),
    organizationPlaceholder: intl.formatMessage({
      id: 'projectsList.createProjectModal.organization.placeholder',
    }),
    visibilityLabel: intl.formatMessage({
      id: 'projectsList.createProjectModal.visibility.label',
    }),
    visibilityEnabled: intl.formatMessage({
      id: 'projectsList.createProjectModal.visibility.enabled',
    }),
    visibilityDisabled: intl.formatMessage({
      id: 'projectsList.createProjectModal.visibility.disabled',
    }),
  };

  // message overrides for the api workflow dialog
  const apiWorkflowDialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: intl.formatMessage({
      id: 'projectsList.createProjectModal.cancelButton',
    }),
    loadingSaveButtonText: intl.formatMessage({ id: 'projectsList.createProjectModal.saveButton' }),

    entryCancelButtonText: intl.formatMessage({
      id: 'projectsList.createProjectModal.cancelButton',
    }),
    entrySaveButtonText: intl.formatMessage({ id: 'projectsList.createProjectModal.saveButton' }),

    submittingCancelButtonText: intl.formatMessage({
      id: 'projectsList.createProjectModal.cancelButton',
    }),
    submittingSaveButtonText: intl.formatMessage({
      id: 'projectsList.createProjectModal.submitting',
    }),

    successCloseButtonText: intl.formatMessage({
      id: 'projectsList.createProjectModal.closeButton',
    }),

    submittingErrorTitle: intl.formatMessage({
      id: 'projectsList.createProjectModal.submittingError',
    }),

    successTitle: intl.formatMessage({ id: 'projectsList.createProjectModal.success.title' }),
    successText: intl.formatMessage(
      { id: 'projectsList.createProjectModal.success.text' },
      { projectName: projectNameTrimmed }
    ),
  };

  const isReleasesLoading =
    _isReleasesQueryLoading || _isReleasesQueryUninitialized || _isReleasesQueryFetching;
  return {
    title: intl.formatMessage({ id: 'projectsList.createProjectModal.title' }),
    closeCreateProjectModal: closeDialog,
    createProjectModalContentMessages,
    apiWorkflowDialogMessages,
    releasesError,
    organizations: organizationsData?.data,
    organizationsError,
    selectedOrganization,
    onChangeOrganization,
    isVisible,
    onChangeIsVisible,
    onRetryInitialLoadingFetch,
    releases: _releasesData?.data,
    projectName,
    onChangeProjectName,
    selectedRelease,
    onChangeRelease,
    onSubmit,
    onRedirectAfterSubmit,
    isCreateProjectLoading,
    createProjectError,
    isValidToSubmit: projectNameTrimmed.length > 0 && selectedRelease !== undefined,
    isInternalUser,
    showVisibility:
      isInternalUser &&
      selectedOrganization?.displayValue?.toString().toLowerCase() !==
        organizationName?.toLowerCase(),
    isLoading:
      isReleasesLoading ||
      isOrganizationsLoading ||
      (isInternalUser && selectedOrganization === undefined),
  };
};
